import React, { useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Paper, 
  Button, 
  Stepper, 
  Step, 
  StepLabel, 
  StepContent,
  Avatar,
  Stack
} from '@mui/material';
import { 
  Search as SearchIcon, 
  AppRegistration as RegisterIcon, 
  AddBusiness as CreateIcon, 
  QrCodeScanner as QrIcon, 
  CheckCircle as SuccessIcon,
  Security as SecurityIcon,
  HelpOutline as HelpIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  Storefront as StoreIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const HowItWorksPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openHelp = () => document.dispatchEvent(new Event('krillou:open-help'));

  const steps = [
    {
      label: t('howItWorksPage.steps.step1Title'),
      description: t('howItWorksPage.steps.step1Desc'),
      icon: (
      <Box
        sx={{
          backgroundColor: 'primary.main',
          color: 'white',
          borderRadius: '50%',
          width: 64,
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <RegisterIcon fontSize="large" />
      </Box>
    ),
    },
    {
      label: t('howItWorksPage.steps.step2Title'),
      description: t('howItWorksPage.steps.step2Desc'),
      icon: (
        <Box
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '50%',
            width: 64,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CreateIcon fontSize="large" />
        </Box>
      ),
    },
    {
      label: t('howItWorksPage.steps.step3Title'),
      description: t('howItWorksPage.steps.step3Desc'),
      icon: (
        <Box
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '50%',
            width: 64,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <SearchIcon fontSize="large" />
        </Box>
      ),
    },
    {
      label: t('howItWorksPage.steps.step4Title'),
      description: t('howItWorksPage.steps.step4Desc'),
      icon: (
        <Box
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '50%',
            width: 64,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <QrIcon fontSize="large" />
        </Box>
      ),
    },
    {
      label: t('howItWorksPage.steps.step5Title'),
      description: t('howItWorksPage.steps.step5Desc'),
      icon: (
        <Box
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '50%',
            width: 64,
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <SuccessIcon fontSize="large" />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', pb: 10 }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          bgcolor: 'primary.main', 
          color: 'primary.contrastText', 
          py: { xs: 8, md: 12 },
          textAlign: 'center',
          mb: 8
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
            {t('howItWorksPage.heroTitle')}
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
            {t('howItWorksPage.heroSubtitle')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 6, maxWidth: 700, mx: 'auto', opacity: 0.8 }}>
            {t('howItWorksPage.description')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button 
              variant="contained" 
              color="success" 
              size="large"
              onClick={() => navigate('/search')}
              startIcon={<SearchIcon />}
              sx={{ px: 4, py: 1.5, borderRadius: 2, color: 'white',border: '2px solid white' }}
            >
              {t('howItWorksPage.ctaExplore')}
            </Button>
            <Button 
              variant="outlined" 
              color="inherit" 
              size="large"
              onClick={openHelp}
              startIcon={<HelpIcon />}
              sx={{ px: 4, py: 1.5, borderRadius: 2 }}
            >
              {t('howItWorksPage.ctaHelp')}
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Main Flow (Stepper) */}
        <Box sx={{ mb: 12 }}>
          <Typography variant="h4" align="center" gutterBottom fontWeight="bold">
            {t('howItWorksPage.timelineTitle')}
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
            {t('howItWorksPage.timelineSubtitle')}
          </Typography>
          
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Stepper orientation="vertical">
                {steps.map((step, index) => (
                  <Step key={index} active={true}>
                    <StepLabel
                      StepIconComponent={() => (
                        <Avatar sx={{ bgcolor: 'primary.light', width: 50, height: 50 }}>
                          {step.icon}
                        </Avatar>
                      )}
                    >
                      <Typography variant="h6" fontWeight="bold" sx={{ ml: 2 }}>
                        {step.label}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, ml: 2, mt: 1 }}>
                        <Typography variant="body1" color="text.secondary">
                          {step.description}
                        </Typography>
                      </Paper>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Grid>
          </Grid>
        </Box>

        {/* Roles Section */}
        <Grid container spacing={4} sx={{ mb: 12 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, height: '100%', borderRadius: 3, textAlign: 'center', borderTop: '4px solid', borderColor: 'primary.main' }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 60, height: 60, mx: 'auto', mb: 2 }}>
                <PersonIcon />
              </Avatar>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                {t('howItWorksPage.personas.clientTitle')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('howItWorksPage.personas.clientDesc')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, height: '100%', borderRadius: 3, textAlign: 'center', borderTop: '4px solid', borderColor: 'secondary.main' }}>
              <Avatar sx={{ bgcolor: 'secondary.main', width: 60, height: 60, mx: 'auto', mb: 2 }}>
                <StoreIcon />
              </Avatar>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                {t('howItWorksPage.personas.ownerTitle')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('howItWorksPage.personas.ownerDesc')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Security Highlights */}
        <Box sx={{ bgcolor: 'black', color: 'white', p: { xs: 4, md: 8 }, borderRadius: 4, mb: 10 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                {t('howItWorksPage.securityTitle')}
              </Typography>
              <Stack spacing={3} sx={{ mt: 4 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <SecurityIcon color="primary" />
                  <Typography variant="h6">{t('howItWorksPage.securityDesc1')}</Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <ChatIcon color="primary" />
                  <Typography variant="h6">{t('howItWorksPage.securityDesc2')}</Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <SuccessIcon color="primary" />
                  <Typography variant="h6">{t('howItWorksPage.securityDesc3')}</Typography>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box 
                component="img" 
                src="/assets/security-shield.png"
                sx={{ maxWidth: '100%', height: 'auto', opacity: 0.8 }}
                alt="Security"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Final CTA */}
        <Box textAlign="center" sx={{ py: 6 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {t('howItWorksPage.helpTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            {t('howItWorksPage.helpDesc')}
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            onClick={openHelp}
            sx={{ borderRadius: 2, px: 6 }}
          >
            {t('howItWorksPage.ctaHelp')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorksPage;